/**
 * Quantity, in lots or in rupees.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO WAYS TO SAY THE SAME THING
 *
 * A desk sizes either by LOTS ("2 lots of NIFTY") or by EXPOSURE ("₹50 L of
 * premium"). The second is the one that needs arithmetic, and it is the one
 * where a wrong answer is expensive, so it lives here as a pure function
 * rather than inline in a component.
 *
 * ROUNDS DOWN, ALWAYS.
 *
 * ₹50 L at a premium of ₹4,292 per lot is 11.6 lots. Rounding to 12 spends
 * ₹51.5 L — more than the number the user typed. A budget is a ceiling, not
 * a target, and the one direction that can never surprise someone is down.
 *
 * ZERO LOTS IS AN ANSWER.
 *
 * A budget smaller than one lot buys nothing. Reporting 0 with the reason is
 * honest; silently rounding up to 1 spends money the user did not agree to.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { lotsForMargin } from '@apna/shared'

export type QuantityMode = 'LOTS' | 'RUPEES'

export interface AutoLotsInput {
  /** The rupee budget the user typed. */
  budget: number
  /** Premium per UNIT — the ATM option's LTP, not the lot's cost. */
  premiumPerUnit: number
  /** Units in one lot, from the catalogue. */
  lotSize: number
}

export type AutoLots =
  | { ok: true; lots: number; costPerLot: number; totalCost: number }
  | { ok: false; why: string; costPerLot: number | null }

/** How many whole lots a budget buys. */
export function autoLotsFor({ budget, premiumPerUnit, lotSize }: AutoLotsInput): AutoLots {
  if (!(lotSize > 0)) return { ok: false, why: 'no lot size for this contract', costPerLot: null }
  if (!(premiumPerUnit > 0)) {
    /*
     * No premium means no basis. The ATM contract has not ticked, or the
     * market is shut — either way, guessing a lot count from a price we do
     * not have is how a budget turns into an unintended position.
     */
    return { ok: false, why: 'no ATM premium yet - cannot size by rupees', costPerLot: null }
  }

  const costPerLot = premiumPerUnit * lotSize
  if (!(budget > 0)) return { ok: false, why: 'enter a budget', costPerLot }

  const lots = Math.floor(budget / costPerLot)
  if (lots < 1) {
    return {
      ok: false,
      why: `one lot costs ${inr(costPerLot)} - more than the budget`,
      costPerLot,
    }
  }
  return { ok: true, lots, costPerLot, totalCost: lots * costPerLot }
}

/**
 * Indian digit grouping, in the units a desk reads.
 *
 * Crore and lakh rather than millions: every number on this screen is read
 * by someone who thinks in them, and "5.00 Cr" is instantly checkable where
 * "50,000,000" has to be counted.
 */
export function compactInr(value: number): string {
  const n = Math.abs(value)
  if (n >= 1_00_00_000) return `${sign(value)}${(n / 1_00_00_000).toFixed(2)} Cr`
  if (n >= 1_00_000) return `${sign(value)}${(n / 1_00_000).toFixed(2)} L`
  if (n >= 1_000) return `${sign(value)}${(n / 1_000).toFixed(1)} K`
  return `${sign(value)}${n.toFixed(0)}`
}

const sign = (v: number): string => (v < 0 ? '-' : '')

/** Full rupees with Indian grouping, for a figure that must be exact. */
export const inr = (value: number): string =>
  `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

/** The quick-pick budgets on the rupee control. */
export const BUDGET_PRESETS = [
  { label: '25L', value: 25_00_000 },
  { label: '50L', value: 50_00_000 },
  { label: '1Cr', value: 1_00_00_000 },
  { label: '5Cr', value: 5_00_00_000 },
] as const

/** What one click should send, or why it cannot be sized. */
export type ClickLots = { ok: true; lots: number } | { ok: false; why: string }

export interface ClickLotsInput {
  mode: QuantityMode
  /** The stepper's value, used in LOTS mode. */
  lots: number
  /** RUPEES mode only: has the amount been armed with OK? */
  armed: boolean
  budget: number
  /** Which way the click trades. Buying costs premium; selling blocks margin. */
  side: 'BUY' | 'SELL'
  /** The clicked contract's OWN premium per unit. */
  premiumPerUnit: number
  /**
   * Initial margin for ONE LOT of this contract, sold — `estimateMargin`'s
   * answer, or null when it could not be computed.
   *
   * Required for a SELL and ignored for a BUY. A budget divided by premium
   * sizes a short roughly fifteen times too large, because the premium is
   * what the trade COLLECTS and the margin is what it BLOCKS.
   */
  marginPerLot?: number | null
  lotSize: number
  /** The order path's ceiling, so this refuses before the server does. */
  maxLots: number
}

/**
 * How many lots one click on one contract sends.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CONTRACT'S OWN PREMIUM, NOT THE ATM'S
 *
 * The same budget buys more of a cheap strike than a dear one — that is the
 * point of sizing by money, and using one reference premium for the whole
 * ladder would quietly place a far wing at the size of an ATM option.
 *
 * REFUSES RATHER THAN CLAMPING. A cheap strike and a large budget can ask
 * for six figures of lots. Sending the cap instead would be placing a
 * different trade from the one the budget described, on a path where the
 * click is already the confirmation.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function clickLots(input: ClickLotsInput): ClickLots {
  if (input.mode === 'LOTS') {
    return input.lots > 0 ? { ok: true, lots: input.lots } : { ok: false, why: 'Set a size first.' }
  }
  if (!input.armed) return { ok: false, why: 'Set an amount and press OK first.' }

  /*
   * SELLING IS SIZED ON MARGIN, NOT PREMIUM.
   *
   * A short NIFTY option blocks six figures of initial margin and collects
   * a few thousand rupees of premium. Dividing the budget by the premium —
   * which is what this did — sized a short roughly fifteen times too
   * large, and the broker's RMS was the only thing standing between that
   * and the position.
   */
  if (input.side === 'SELL') {
    const perLot = input.marginPerLot
    if (perLot === null || perLot === undefined || !(perLot > 0)) {
      return { ok: false, why: 'margin cannot be estimated for this strike yet' }
    }
    const lots = lotsForMargin(input.budget, perLot)
    if (lots < 1) {
      return { ok: false, why: `one lot blocks about ${inr(perLot)} - more than the budget` }
    }
    return lots > input.maxLots ? tooMany(lots, input.maxLots) : { ok: true, lots }
  }

  const auto = autoLotsFor({
    budget: input.budget,
    premiumPerUnit: input.premiumPerUnit,
    lotSize: input.lotSize,
  })
  if (!auto.ok) return { ok: false, why: auto.why }
  return auto.lots > input.maxLots ? tooMany(auto.lots, input.maxLots) : { ok: true, lots: auto.lots }
}

const tooMany = (lots: number, maxLots: number): ClickLots => ({
  ok: false,
  why: `That budget covers ${lots.toLocaleString('en-IN')} lots; the limit is ${maxLots.toLocaleString('en-IN')}.`,
})
